import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Image,
  ListItem,
  Show,
  UnorderedList,
  theme,
  Text,
} from '@everlywell/ui-kit';
import HorizontalProductCardV2 from 'components/HorizontalProductCardV2';
import { createSharedButtonProps } from 'components/PageSections/utils';
import PriceV2 from 'components/PricingV2/PriceV2';
import RichTextWrapper from 'components/RichTextWrapper';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { MouseEvent } from 'react';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import useMdpModals from 'utils/hooks/useMdpModals';
import { ImageType } from 'utils/types';
import { CallToAction, ContentfulRichText } from 'utils/types/contentfulTypes';

import BulletPointWithIcon, {
  BulletPointWithIconProps,
} from '../PageSections/components/BulletPointWithIcon';

export type ShapeBannerProduct = {
  slug: string;
  displayName: string;
  price: number;
  boxImage: ImageType | undefined;
};

export type ShapeBannerProps = {
  title?: string | null;
  description?: ContentfulRichText | null;
  bulletPoints?: BulletPointWithIconProps[] | null;
  cta?: CallToAction | null;
  desktopImage?: ImageType | null;
  desktopImageAltText?: string | null;
  mobileImage?: ImageType | null;
  mobileImageAltText?: string | null;
  backgroundColor?: string | null;
  products?: ShapeBannerProduct[] | null;
};

const LARGE_TABLET_HEIGHT = '420px';
const SMALL_DESKTOP_HEIGHT = '500px';
const LARGE_DESKTOP_HEIGHT = '580px';

/**
 * ShapeBanner is a component that displays a banner with an image, a description, and a call to action button.
 * It can have a description, bullet points, and a call to action.
 * @param {ShapeBannerProps} props - The props of the component.
 * @returns {JSX.Element | null} - The component.
 */
export default function ShapeBanner({
  title,
  description,
  cta,
  desktopImage,
  mobileImage,
  backgroundColor,
  bulletPoints,
  products,
  desktopImageAltText,
  mobileImageAltText,
}: ShapeBannerProps): JSX.Element | null {
  const { handleMdpCtaClick } = useMdpModals();

  const handleButtonClick = (clickEvent: MouseEvent<HTMLButtonElement>) => {
    handleMdpCtaClick({
      event: clickEvent,
      ctaUrl: cta?.url || '',
    });

    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'Shape Banner',
        ctaText: cta?.label,
      },
    });
  };

  const trackCardClick = (productName: string) => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'Shape Banner',
        cta: productName,
      },
    });
  };

  const sharedButtonProps: ButtonProps = {
    variant: 'primary',
    onClick: handleButtonClick,
    id: `${title}-cta`,
    ...createSharedButtonProps(cta?.url),
  };

  return (
    <Flex
      flexDirection={['column', null, 'row']}
      height={[
        null,
        null,
        LARGE_TABLET_HEIGHT,
        SMALL_DESKTOP_HEIGHT,
        LARGE_DESKTOP_HEIGHT,
      ]}
      backgroundColor={backgroundColor || undefined}
      borderRadius="lg"
      overflow="hidden"
      position="relative"
    >
      {/* Mobile image*/}
      <Show breakpoint={`(max-width: ${theme.breakpoints.largeTablet})`}>
        <Image
          as={GatsbyImage}
          image={mobileImage?.gatsbyImageData}
          height={['200px', null, '100%']}
          maxWidth="385px"
          margin="0 auto"
          alt={mobileImageAltText || ''}
        />
      </Show>

      {/* Container for text content, bullet points, product cards and cta */}
      <Box
        width={[null, null, '55%', '40%']}
        marginTop={[6, null, 'auto']}
        marginBottom={[null, null, 'auto']}
        padding={[6, null, 6, 12]}
        height="fit-content"
      >
        {description ? (
          <Box>
            <RichTextWrapper contentfulRichText={description}></RichTextWrapper>
          </Box>
        ) : null}

        {bulletPoints ? (
          <UnorderedList
            display="flex"
            flexDirection="column"
            gap="2"
            listStyleType="none"
            marginTop="4"
            marginLeft="0"
            marginBottom={[null, null, 6, 10]}
          >
            {bulletPoints?.map((bullet) => (
              <ListItem key={`${bullet.text}`}>
                <BulletPointWithIcon {...bullet} />
              </ListItem>
            ))}
          </UnorderedList>
        ) : null}

        {/* Container for product. Positioned absolute on desktop */}
        <Flex
          gap={2}
          flexDirection={['column', null, 'row']}
          width={[null, null, '60%', '57%']}
          position={[null, null, 'absolute']}
          zIndex="1"
          right="0"
          bottom="0"
          paddingTop={[4, null, 0]}
          paddingRight={[null, null, 6, 12]}
          paddingBottom={[4, null, 6, 12]}
        >
          {products?.map((product) => {
            if (!product) {
              return null;
            }

            const dollarAmount = product?.price?.toString().split('.')[0];

            return (
              <HorizontalProductCardV2
                key={product.displayName}
                image={
                  product.boxImage ? (
                    <GatsbyImage
                      image={product.boxImage.gatsbyImageData}
                      alt={`${product?.displayName} box image`}
                      style={{
                        width: '120px',
                        height: 'auto',
                        zIndex: '1',
                      }}
                    ></GatsbyImage>
                  ) : null
                }
                circleBackgroundColor={backgroundColor || undefined}
                linkTo={`/products/${product.slug}/`}
                pricing={
                  <>
                    <PriceV2 dollarAmount={dollarAmount}></PriceV2>
                  </>
                }
                onClick={() => trackCardClick(product.displayName)}
              >
                <Text
                  fontSize={['md', 'lg']}
                  color="tints.black"
                  fontWeight="medium"
                >
                  {product.displayName}
                </Text>
              </HorizontalProductCardV2>
            );
          })}
        </Flex>

        {cta && cta?.label ? (
          <Button {...sharedButtonProps} width={['100%', null, 'fit-content']}>
            {cta.label}
          </Button>
        ) : null}
      </Box>

      {/* Desktop image */}
      <Show breakpoint={`(min-width: ${theme.breakpoints.largeTablet})`}>
        <Image
          as={GatsbyImage}
          image={desktopImage?.gatsbyImageData}
          width={[null, null, '55%', '60%']}
          height={[
            null,
            null,
            LARGE_TABLET_HEIGHT,
            SMALL_DESKTOP_HEIGHT,
            LARGE_DESKTOP_HEIGHT,
          ]}
          alt={desktopImageAltText || ''}
        />
      </Show>
    </Flex>
  );
}
